/*
  Native route transitions (HAP-1887, parent HAP-1884 Phase 1.1).

  Gives client-side route changes a native iOS-Settings "drill" feel using the
  GPU-accelerated View Transitions API. Direction is keyed off the navigation
  type: a push (link / router.push) drills the new screen in from the right; a
  pop (browser back) slides the current screen off to the right to reveal the
  previous one.

  The actual animation lives in CSS (`::view-transition-*(root)` keyframes in
  index.css) and is driven by the `data-route-transition` attribute we set on
  <html>. We only orchestrate here: detect support, detect direction, wrap the
  router's DOM swap in `document.startViewTransition`, and clean up.

  Graceful degradation:
    • No View Transitions support  → we no-op and `store.viewTransitions` stays
      false, so App.vue keeps applying the existing tailwindcss-motion fallback
      classes.
    • Reduced motion (`store.reducedMotion`) → skip the transition entirely.
    • Desktop (md+)                → skip; the drill is a mobile affordance and
      matches the existing per-component motion which only runs on !md.

  Durations/easings come from the P0.2 motion tokens (CSS vars) so the whole
  app shares one motion language.
*/
import { nextTick } from 'vue'

const supportsViewTransitions =
	typeof document !== 'undefined' && typeof document.startViewTransition === 'function'

export function installRouteTransitions(router, store, breakpoints) {
	// Let the rest of the app (App.vue) know whether to suppress the
	// tailwindcss-motion fallback classes in favor of native view transitions.
	store.viewTransitions = supportsViewTransitions

	if (!supportsViewTransitions) return

	// Direction tracking. Vue Router keeps a monotonic `position` counter in
	// history.state. Browser back/forward fires `popstate` BEFORE the router
	// guards run, with history.state already pointing at the destination — so we
	// compare its position against the last known one to tell back from forward.
	// A programmatic push fires no popstate, so `popDirection` stays null and we
	// treat it as a forward drill.
	let lastPosition = window.history.state?.position ?? 0
	let popDirection = null // null = push (forward); else 'back' | 'forward'

	window.addEventListener('popstate', () => {
		const next = window.history.state?.position ?? 0
		popDirection = next < lastPosition ? 'back' : 'forward'
	})

	// beforeResolve runs after async route components have been resolved, so the
	// destination component's chunk is already loaded by the time we start the
	// transition — the new view paints (almost) immediately rather than flashing
	// the Suspense spinner.
	router.beforeResolve((to, from) => {
		if (!from.matched.length) return // initial app load — nothing to animate
		if (to.path === from.path) return // same page (e.g. guard redirect bounce)
		if (store.reducedMotion) return
		if (breakpoints.md) return // mobile-only drill

		// Direction detection uses two signals, either of which marks a back-style
		// navigation so the drill reverses (new screen comes in from the LEFT):
		//
		//   1. popDirection — a genuine browser/history pop (back chevrons that call
		//      router.go(-1)/back(), hardware/gesture back in the app wrapper).
		//   2. store.openType — the app's own up-front navigational intent, set on
		//      the click BEFORE navigation runs. Back affordances set 'slideRight'
		//      (header chevron, home logo) or 'slideDown' (closing a slide-up); the
		//      afterEach hook that consumes openType runs AFTER this guard, so the
		//      intent is still readable here.
		//
		// Honoring intent as well as history makes back reliable inside the WebView
		// "app" wrapper, where history-position detection alone proved fragile and
		// where some back affordances navigate via router.push rather than a pop.
		const backIntent = store.openType === 'slideRight' || store.openType === 'slideDown'
		const direction = popDirection === 'back' || backIntent ? 'back' : 'forward'

		return new Promise((resolve) => {
			document.documentElement.dataset.routeTransition = direction
			const transition = document.startViewTransition(() => {
				// Unblock navigation so the router-view swaps the component, then
				// wait one tick for Vue to flush the new view into the DOM before
				// the API captures the "after" snapshot.
				resolve()
				return nextTick()
			})
			transition.finished.finally(() => {
				delete document.documentElement.dataset.routeTransition
			})
		})
	})

	router.afterEach(() => {
		lastPosition = window.history.state?.position ?? lastPosition
		popDirection = null
	})
}

export default installRouteTransitions
